<template>
  <ValidationObserver ref="selfScheduleSlots">
    <h2 class="mb-5 mt-5">Self-Schedule Details</h2>
    <div class="d-flex align-center">
      <v-icon color="primary" class="mr-2" large>
        mdi-clipboard-text-outline
      </v-icon>
      <div clas="d-flex flex-column mb-5">
        <h3>{{ interviewFormat.interviewName | capitalize }}</h3>
        <span class="secondary-text">
          {{ interviewFormat.duration }}
          {{ pluralize("minutes", interviewFormat.duration) }}
        </span>
      </div>
    </div>
    <ValidationProvider v-slot="{ errors }" rules="required">
      <Select
        label="Interviewers"
        autocomplete
        class="mt-4"
        :error-messages="showValidationErrors ? errors : ''"
        :value="slotsData.interviewers"
        @change="val => handleSlotsDataChange('interviewers', val)"
        :disabled="isFetchingInterviewers"
        :items="formattedInterviewers"
        item-text="fullName"
        chips
        small-chips
        :loading="isFetchingInterviewers"
        placeholder="Select Interviewers"
        multiple
        return-object
      >
        <template v-slot:selection="{ data }">
          <v-chip close @click:close="unselectInterviewer(data.item.id)">
            <v-icon>mdi-account</v-icon>
            {{ data.item.fullName }}
          </v-chip>
        </template>
        <template v-slot:item="{ item }">
          <StatusItem class="user-select-item">
            <template v-slot:icon><v-icon>mdi-account</v-icon></template>
            {{ item.fullName }}
          </StatusItem>
        </template>
      </Select>
    </ValidationProvider>
    <ValidationProvider
      v-if="!disableLocation"
      v-slot="{ errors }"
      rules="required"
    >
      <Select
        autocomplete
        v-model="slotsData.location"
        @input="val => handleSlotsDataChange('location', val)"
        :error-messages="showValidationErrors ? errors : ''"
        height="50"
        class="mt-0"
        label="Location"
        placeholder="Select location"
        item-text="label"
        item-value="value"
        :items="formattedLocations"
        :loading="isFetchingLocations"
        :disabled="isFetchingLocations"
      />
    </ValidationProvider>
    <v-checkbox
      type="checkbox"
      hide-details
      @change="
        value => {
          handleSlotsDataChange('location', null), (disableLocation = value);
        }
      "
      label="Interview is taking place over the phone or virtually"
      v-model="disableLocation"
      class="shrink mt-0 mb-5"
    />
    <div v-if="size(slotsData.existingSlots)" class="mb-5">
      <p class="font-weight-bold">Existing Slots</p>
      <GroupedSlotList
        :slots="slotsData.existingSlots"
        @onRemoveSlot="removeSlot"
        showRemoveButton
        :key="existingSlotListId"
      />
    </div>
    <h3 class="mb-3">New Slots</h3>
    <GroupedSlotList
      :slots="slotsData.interviewSlots"
      @onRemoveSlot="removeSlot"
      showRemoveButton
    />
    <PrimaryButton @click.native="onAddSlots" fullWidth class="mt-5">
      Add Slots
    </PrimaryButton>
    <AddSelfScheduleSlotsDialog
      v-if="isAddSlotsDialogOpen"
      @close="isAddSlotsDialogOpen = false"
      @saveSelectedSlots="saveSelectedSlots"
      :interviewFormat="interviewFormat"
      :interviewers="slotsData.interviewers"
      :externalSelectedSlots="slotsData.interviewSlots"
      @onRemoveSlot="removeSlot"
    />
  </ValidationObserver>
</template>

<script>
import { INTERVIEWS_NAMESPACE } from "@/store/modules/bookings/interviews";
import {
  FETCH_INTERVIEWERS,
  FETCH_INTERVIEW_FORMAT_AVAILABLE_SLOTS
} from "@/store/modules/bookings/interviews/actions";
import { createNamespacedHelpers } from "vuex";
import StatusItem from "@/components/common/StatusItem";
import AddSelfScheduleSlotsDialog from "@/views/bookings/BookingInterviews/components/dialogs/SelfScheduleInvites/SelfScheduleInvite/steps/SelfScheduleSlots/AddSelfScheduleSlotsDialog";
import GroupedSlotList from "@/views/bookings/BookingInterviews/components/dialogs/SelfScheduleInvites/SelfScheduleInvite/steps/SelfScheduleSlots/components/GroupedSlotList";
import Select from "@/components/common/Select";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { copyState } from "@/utils/helpers";
import { getFullName } from "@/utils/users";
import pluralize from "pluralize";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import { map, size, filter, merge } from "lodash";
import generateRandomId from "uuid/v4";
import { getFormattedLocation } from "@/utils/locations";
import { DATA_NAMESPACE } from "@/store/modules/data";
import { FETCH_LOCATIONS } from "@/store/modules/data/actions";
import {
  GET_IS_FETCHING_DATA,
  GET_LOCATIONS
} from "@/store/modules/data/getters";

const {
  mapActions: mapInterviewActions,
  mapState: mapInterviewState
} = createNamespacedHelpers(INTERVIEWS_NAMESPACE);

const {
  mapActions: mapDataActions,
  mapGetters: mapDataGetters
} = createNamespacedHelpers(DATA_NAMESPACE);

const INITIAL_SLOT_DATA = {
  interviewers: [],
  interviewSlots: [],
  location: "",
  exsitingSlots: []
};

export default {
  name: "SelfScheduleSlots",
  components: {
    StatusItem,
    Select,
    ValidationProvider,
    ValidationObserver,
    AddSelfScheduleSlotsDialog,
    PrimaryButton,
    GroupedSlotList
  },
  props: {
    interviewFormat: {
      type: Object,
      required: true
    }
  },
  created() {
    this.adjustExistingSlots();
    this.fetchInterviewers(this.interviewFormatId);
    this.fetchLocations({ filter: { "not-primary": true } });
  },
  data() {
    return {
      disableLocation: false,
      slotsData: merge({}, INITIAL_SLOT_DATA),
      showValidationErrors: false,
      isAddSlotsDialogOpen: false,
      existingSlotListId: generateRandomId()
    };
  },
  computed: {
    ...mapDataGetters({
      isFetchingLocations: GET_IS_FETCHING_DATA,
      locations: GET_LOCATIONS
    }),
    ...mapInterviewState(copyState(["interviewers", "isFetchingInterviewers"])),
    interviewFormatId() {
      return this.interviewFormat.id;
    },
    interviewerIds() {
      return map(this.slotsData.interviewers, ({ id }) => Number(id));
    },
    formattedLocations() {
      return this.locations.map(location => ({
        label: getFormattedLocation(location),
        value: location
      }));
    },
    formattedInterviewers() {
      return map(this.interviewers, interviewer => ({
        ...interviewer,
        fullName: getFullName(interviewer)
      }));
    }
  },
  methods: {
    ...mapInterviewActions({
      fetchInterviewSlots: FETCH_INTERVIEW_FORMAT_AVAILABLE_SLOTS,
      fetchInterviewers: FETCH_INTERVIEWERS
    }),
    ...mapDataActions({
      fetchLocations: FETCH_LOCATIONS
    }),
    adjustExistingSlots() {
      this.slotsData.existingSlots = filter(
        this.interviewFormat.interviewSlots,
        ({ filled }) => !filled
      );
      this.$emit("onSlotsDataChange", this.slotsData);
    },
    async onAddSlots() {
      const isFormValid = await this.isFormValid();
      if (isFormValid) {
        this.isAddSlotsDialogOpen = true;
      }
    },
    removeSlot(slotToRemove) {
      if (slotToRemove.existing) {
        this.slotsData.existingSlots = filter(
          this.slotsData.existingSlots,
          ({ id }) => id !== slotToRemove.id
        );
        // to refresh slot list
        this.existingSlotListId = generateRandomId();
        this.$emit("onRemoveSlot", slotToRemove);
      } else {
        this.slotsData.interviewSlots = filter(
          this.slotsData.interviewSlots,
          ({ startTime, endTime }) =>
            startTime !== slotToRemove.startTime &&
            endTime !== slotToRemove.endTime
        );
        this.$emit("onSlotsDataChange", this.slotsData);
      }
    },
    saveSelectedSlots(val) {
      this.isAddSlotsDialogOpen = false;
      this.handleSlotsDataChange("interviewSlots", val);
    },
    updateSlotsData(key, value) {
      this.slotsData[key] = value;
    },
    async handleSlotsDataChange(key, val) {
      this.updateSlotsData(key, val);
      this.$emit("onSlotsDataChange", this.slotsData);
    },
    unselectInterviewer(interviewerId) {
      this.handleSlotsDataChange(
        "interviewers",
        filter(
          this.slotsData.interviewers,
          ({ id }) => Number(id) !== Number(interviewerId)
        )
      );
    },
    async isFormValid() {
      this.showValidationErrors = true;
      const isValid = await this.$refs.selfScheduleSlots.validate();
      return isValid;
    },
    pluralize,
    size
  },
  watch: {
    interviewFormat(newValue, prevValue) {
      if (newValue.id !== prevValue.id) {
        this.showValidationErrors = false;
        this.slotsData = merge({}, INITIAL_SLOT_DATA);
        this.fetchInterviewers(this.interviewFormatId);
        this.adjustExistingSlots();
      }
    }
  }
};
</script>

<style lang="scss"></style>
